import { Alert, AlertDescription, AlertTitle, Spinner } from '@design-system';
import { WAG_SUPPORT_EMAIL } from '@shared';
import { CONSUMER_POLICY_REVIEW_NAMES, isPolicyReviewIncluded } from '@shared/consumerPlan';
import { DetailList } from '../components/figures';
import { StepFrame } from '../components/step-frame';
import { useConsumerFlow } from '../hooks/use-consumer-flow';
import { usePolicyReviewStatus, type PolicyReviewPayment } from '../hooks/use-policy-review';
import { money } from '../lib/format';
import type { ConsumerStepProps } from './step-props';

const PAYMENT_COPY: Record<PolicyReviewPayment, { title: string; subtitle: string }> = {
  paid: { title: 'We’ve received your review request.', subtitle: 'We’ve emailed you a confirmation with what happens next.' },
  confirming: { title: 'Confirming your payment…', subtitle: 'This usually takes a few seconds after you pay.' },
  unpaid: {
    title: 'We haven’t received your payment.',
    subtitle: 'Your review starts once the one-time fee is paid.',
  },
};

export function ReviewStatusStep({ clientId }: ConsumerStepProps) {
  const { goTo } = useConsumerFlow(clientId);
  const { plan, review, payment } = usePolicyReviewStatus(clientId);

  const exploreCoverage = () => goTo(plan?.selectedQuoteKey ? 'coverage' : 'options');

  if (!review) {
    return (
      <StepFrame
        step="review-status"
        title="No review requests yet."
        primary={{ label: 'Request a policy review', onClick: () => goTo('existing-policy') }}
      />
    );
  }

  const copy = PAYMENT_COPY[payment];

  return (
    <StepFrame
      step="review-status"
      title={copy.title}
      subtitle={copy.subtitle}
      primary={
        payment === 'unpaid'
          ? { label: 'Finish paying for my review', onClick: () => goTo('review-checkout') }
          : { label: 'Explore coverage options', onClick: exploreCoverage }
      }
      secondary={payment === 'unpaid' ? { label: 'Explore coverage options', onClick: exploreCoverage } : undefined}
    >
      {payment === 'confirming' && (
        <Alert variant="info">
          <Spinner />
          <AlertTitle>Waiting for Stripe</AlertTitle>
          <AlertDescription>If you closed checkout without paying, you can start the review again.</AlertDescription>
        </Alert>
      )}
      <DetailList
        rows={[
          { label: 'Review', value: CONSUMER_POLICY_REVIEW_NAMES[review.reviewType] },
          {
            label: 'One-time fee',
            value: isPolicyReviewIncluded(review)
              ? 'Included with self-funding access'
              : money(review.amountInCents / 100),
          },
          { label: 'Documents', value: review.documentNames.length },
        ]}
      />
      <ul className="flex flex-col gap-3 text-sm">
        <li>
          <strong>Target: 24–48 hours</strong> after complete documents arrive; some reviews take up to 7
          business days.
        </li>
        <li>Our licensed team will ask if any pages or terms need clarification.</li>
        <li>You’ll get your updated plan and PDF by email. Questions? Email {WAG_SUPPORT_EMAIL}.</li>
      </ul>
      <p className="text-muted-foreground text-xs">
        Wealth Assurance Group is Waterlily’s licensed insurance subsidiary, providing insurance support and
        services.
      </p>
    </StepFrame>
  );
}
